/**
 * Scéna súboja nad doskou.
 *
 * Najdôležitejší okamih hry je, keď sa **obe karty otočia naraz** — v papierovej
 * hre ich hráči odhalia v tej istej sekunde a presne to tu napodobňujeme:
 * obe dostanú triedu `is-flipped` v tom istom renderi, takže sa otáčajú spolu.
 *
 * Scéna nič nerozhoduje a nič neblokuje: leží len nad doskou, ruka pod ňou
 * zostáva použiteľná (obranca si z nej práve vyberá kartu).
 */

import type { CardDef } from '@unmatched/engine';
import { content, portraitCard } from '@unmatched/content';

import type { CombatShow } from '../game/useStage.js';
import { Card } from './Card.js';
import { cardImageUrl } from '../cardImage.js';

interface Props {
  show: CombatShow;
  /** Definícia karty podľa ID kusa v zápase. */
  defOf: (cardId: string) => CardDef | null;
  /** Meno hráča (prezývka, v hot-seate postava). */
  nameOf: (player: number) => string;
  /** Kto sa pozerá — útočníkovi aj obrancovi scéna povie „ty". */
  viewer: number;
  /** Súboj stojí uprostred a čaká na rozhodnutie (The Day of Your Doom…). */
  waiting?: string | null;
  onDismiss: () => void;
}

const ORDER = { declared: 0, flip: 1, values: 2, result: 3 } as const;

export function CombatStage({ show, defOf, nameOf, viewer, waiting, onDismiss }: Props) {
  const step = ORDER[show.phase];
  const flipped = step >= ORDER.flip;
  const attackDef = show.attackCard ? defOf(show.attackCard) : null;
  const defenseDef = show.defenseCard ? defOf(show.defenseCard) : null;

  const headline = (() => {
    if (step < ORDER.flip) {
      return show.defender.owner === viewer ? 'Útočia na teba — vyber obranu' : 'Obranca vyberá kartu…';
    }
    if (step < ORDER.values && waiting) return waiting;
    if (step < ORDER.result) return 'Odhalenie';
    if (show.defeated) return `${show.defender.name} — mimo boja`;
    if (!show.damage) return 'Útok odrazený';
    return `${show.damage} ${show.damage === 1 ? 'poškodenie' : show.damage < 5 ? 'poškodenia' : 'poškodení'}`;
  })();

  return (
    <div
      className={`stage stage--${show.phase}${show.defeated && step >= ORDER.result ? ' is-lethal' : ''}`}
      onClick={step >= ORDER.result ? onDismiss : undefined}
      role="status"
      aria-live="polite"
    >
      <div className="stage__panel">
        <span className="stage__eyebrow">
          {show.bonus ? `Bonusový útok · ${show.bonus}` : 'Súboj'}
        </span>

        <div className="stage__duel">
          <Side
            fighter={show.attacker}
            role="útočí"
            player={nameOf(show.attacker.owner)}
            you={show.attacker.owner === viewer}
            hp={show.attacker.hp}
          >
            <Flip
              flipped={flipped}
              front={attackDef ? <Card def={attackDef} large /> : <div className="stage__empty">?</div>}
              cancelled={Boolean(show.attackCard && show.cancelled.includes(show.attackCard))}
            />
            <Value
              visible={step >= ORDER.values}
              value={show.attackValue}
              printed={attackDef?.value ?? null}
            />
          </Side>

          <div className={`stage__vs${step >= ORDER.result ? ' is-hit' : ''}`}>
            {step >= ORDER.result && show.attackValue !== null ? (
              <span className="stage__math">
                {show.attackValue} − {show.defenseValue ?? 0}
              </span>
            ) : (
              'VS'
            )}
          </div>

          <Side
            fighter={show.defender}
            role="bráni sa"
            player={nameOf(show.defender.owner)}
            you={show.defender.owner === viewer}
            hp={step >= ORDER.result && show.hpAfter !== null ? show.hpAfter : show.defender.hp}
            hit={step >= ORDER.result && Boolean(show.damage)}
          >
            <Flip
              flipped={flipped}
              waiting={step < ORDER.flip}
              front={
                show.passed ? (
                  <div className="stage__noDefense">bez obrany</div>
                ) : defenseDef ? (
                  <Card def={defenseDef} large />
                ) : (
                  <div className="stage__empty">?</div>
                )
              }
              cancelled={Boolean(show.defenseCard && show.cancelled.includes(show.defenseCard))}
            />
            <Value
              visible={step >= ORDER.values}
              value={show.passed ? 0 : show.defenseValue}
              printed={show.passed ? 0 : (defenseDef?.value ?? null)}
            />
          </Side>
        </div>

        <div className={`stage__headline${step >= ORDER.result ? ' is-result' : ''}`}>{headline}</div>
        {step >= ORDER.result ? <span className="stage__hint">klikni pre zavretie</span> : null}
      </div>
    </div>
  );
}

function Side({
  fighter,
  role,
  player,
  you,
  hp,
  hit = false,
  children,
}: {
  fighter: CombatShow['attacker'];
  role: string;
  player: string;
  you: boolean;
  hp: number;
  hit?: boolean;
  children: React.ReactNode;
}) {
  const portrait = portraitCard(fighter.defId);
  const icon = content.heroes[fighter.defId]?.icon;
  const ratio = fighter.maxHp > 0 ? Math.max(0, hp / fighter.maxHp) : 0;
  return (
    <div className={`stage__side stage__side--p${fighter.owner}${hit ? ' is-hit' : ''}`}>
      <div className="stage__who">
        <span className="stage__portrait">
          {portrait ? (
            <img src={cardImageUrl(portrait)} alt="" draggable={false} />
          ) : icon ? (
            <img className="is-icon" src={icon} alt="" />
          ) : (
            fighter.name.slice(0, 1)
          )}
        </span>
        <span className="stage__name">
          {fighter.name}
          <em>
            {/* V hot-seate je „hráč" meno postavy — „Sinbad · Sinbad" by bolo zbytočné. */}
            {you ? `${role} · ty` : player === fighter.name ? role : `${role} · ${player}`}
          </em>
        </span>
      </div>
      <div className="stage__hp">
        <span className="stage__hpFill" style={{ width: `${ratio * 100}%` }} />
        <span className="stage__hpValue">
          {hp}/{fighter.maxHp}
        </span>
      </div>
      {children}
    </div>
  );
}

function Flip({
  flipped,
  front,
  waiting = false,
  cancelled = false,
}: {
  flipped: boolean;
  front: React.ReactNode;
  waiting?: boolean;
  cancelled?: boolean;
}) {
  return (
    <div className={`flip${flipped ? ' is-flipped' : ''}${waiting ? ' is-waiting' : ''}`}>
      <div className="flip__inner">
        <div className="flip__face flip__back">
          <div className="card card--back card--large" />
          {waiting ? <span className="flip__ask">?</span> : null}
        </div>
        <div className="flip__face flip__front">
          {front}
          {cancelled ? <span className="flip__stamp">zrušené</span> : null}
        </div>
      </div>
    </div>
  );
}

function Value({
  visible,
  value,
  printed,
}: {
  visible: boolean;
  value: number | null;
  printed: number | null;
}) {
  if (!visible || value === null) return <div className="stage__value is-hidden">0</div>;
  const changed = printed !== null && printed !== value;
  return (
    <div className={`stage__value${changed ? ' is-changed' : ''}`}>
      {value}
      {changed ? <em>vytlačené {printed}</em> : null}
    </div>
  );
}
